import { useEffect, useState } from "react";
import { getColumns, initialSort } from "../ClientRatingOverviewTableConfig";
import { Box } from "@mui/system";
import BasicDatatable from "../../Common/BasicDatatable";
import ProgressSpinner from "../../Common/ProgressSpinner";
import SlideOver from "../../Common/SlideOver";
import ClientInteractionActionView from "../ClientInteractionActionView";
import useDisplaySnackbar from "../../../utils/useDisplaySnackbar";
import useGlobalContext from "../../../context/useGlobalContext";
import { getUserAttributes } from "../../../utils/auth";
import ClientService from "../../../services/clients/ClientService";
import { convertToUTC } from "../../../utils/utils";

const generateRows = (
  accountInsights: any[],
  loggedInEmail: string | null,
  startEndMeetingTime: any,
  isClientInteraction: boolean,
  onClientClick: (clientDomain: string, clientName: string, slideOver: boolean, clientOrgId: string, subOrgId: string) => void,
  highlightedClient: string | null,
  isSlideOverOpen?: boolean,
  parentPageIdentifier?: string,
  slideOverParent?: string,
  customDateRange?: { startDate: string; endDate?: string },
) =>
  accountInsights?.map((item: any) => {
    return {
      id: item.clientDomain,
      name: item.clientName,
      domain: item.clientDomain,
      num_meetings: item.totalInteractions,
      duration: item.interactionDuration,
      last_sentiment:
        item?.lastThreeRatedInteractions
        ?.length > 0 ? item.lastThreeRatedInteractions
        : [],
      lastInteraction: item?.lastInteractionDate,
      classification: item.classification,
      rated: item.rated,
      mrr: item.mrr,
      loggedInEmail: loggedInEmail,
      startEndMeetingTime: startEndMeetingTime,
      onClientClick: onClientClick,
      isClientInteraction: isClientInteraction,
      highlightedClient: highlightedClient,
      parentPageIdentifier: parentPageIdentifier,
      slideOverParent: slideOverParent,
      customDateRange: customDateRange,
      isSlideOverOpen: isSlideOverOpen,
      clientOrgId: item.clientOrgId,
      subOrgId: item.subOrgId,
    };
  });
interface BusinessStatsSlideoverClientProps {
  isSlideOverOpen: boolean;
  slideOverParent?: string;
  activeStat?: string | null;
  selectedUser?: any;
  parentPageIdentifier?: string;
  customDateRange?: { startDate: string; endDate: string };
  userEmail?: string;
}

const BusinessStatsSlideoverClient = ({isSlideOverOpen,
  slideOverParent, 
  activeStat,
  selectedUser,
  parentPageIdentifier,
  customDateRange,
  userEmail,
}: BusinessStatsSlideoverClientProps) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [sort, setSort] = useState(initialSort);
  const { startEndMeetingTime } = useGlobalContext();
  const { showSnackbar } = useDisplaySnackbar();
  const loggedInEmail = getUserAttributes()?.email;
  const [accountInsights, setAccountInsights] = useState<any[]>([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [rows, setRows] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(50);
  const [highlightedClient, setHighlightedClient] = useState<string | null>(null);
  const [clientDomain, setClientDomain] = useState<string>("");
  const isClientInteraction = true;
  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const removeCustomDateRange = true;
  const [ showSpinner, setShowSpinner ] = useState<boolean>(false);
  const [clientOrgId, setClientOrgId] = useState<string>("");
  const [subOrgId, setSubOrgId] = useState<string>("");
  const [clientName, setClientName] = useState<string>("");

  const {
    isSuccess: isClientsMetSuccess,
    isLoading: isClientsMetLoading,
    isFetching: isClientsMetFetching,
    isError: isClientsMetError,
    error: clientsMetError,
    data: clientsMetData,
    refetch: refetchclientsMetData,
  } = ClientService.useGetClientsMet(
    {
      startTime: convertToUTC(customDateRange?.startDate || ""),
      endTime: convertToUTC(customDateRange?.endDate || ""),
      limit: pageSize,
      offset: pageNumber,
      employeePorId: userEmail || selectedUser?.employeePorId || undefined,
      sortBy: sort?.sort_field,
      sortOrder: sort?.sort_order,
    },
    isSlideOverOpen && !!customDateRange?.startDate && !!customDateRange?.endDate,
  );
useEffect(() => {
  if (isSlideOverOpen && customDateRange?.startDate && customDateRange?.endDate) {
    refetchclientsMetData();
  }
}, [isSlideOverOpen, startEndMeetingTime.start_time, startEndMeetingTime.end_time]);

  // handle api data
  useEffect(() => {
    setShowSpinner(isClientsMetLoading || isClientsMetFetching)
    if (isClientsMetSuccess) {
      const clientMetList = clientsMetData?.clients || [];
      setAccountInsights(clientMetList);
      setTotalRecord(clientsMetData?.totalRecords);
    } else if (isClientsMetError) {
      setAccountInsights([]);
      showSnackbar("error", clientsMetError?.message || "Failed to load data.");
    }   
  }, [clientsMetData, isClientsMetError, clientsMetError, showSnackbar, startEndMeetingTime]);

  // handle sorting + pagination
  useEffect(() => {
    const filteredRows = generateRows(
      accountInsights,
      loggedInEmail,
      customDateRange,
      isClientInteraction,
      onClientClick,
      highlightedClient,
      isSlideOverOpen,
      parentPageIdentifier,
      slideOverParent,
      customDateRange,
    );

    setRows(filteredRows);
  }, [accountInsights, sort, loggedInEmail, customDateRange]);

  // highlight logic
  useEffect(() => {
    const selected = sessionStorage.getItem("selectedClient");
    if (selected) {
      setHighlightedClient(selected);
      const timer = setTimeout(() => {
        setHighlightedClient(null);
        sessionStorage.removeItem("selectedClient");
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const onSortingChange = (sorting: any[]) => {
    if (sorting && sorting.length > 0) {
      setSort({
        sort_field: sorting[0].id,
        sort_order: sorting[0].desc === true ? "desc" : "asc",
      });
    }
  };

  const onClientClick = (clientDomain: string, clientName: string, slideOver: boolean, clientOrgId: string, subOrgId: string) => {
    setClientDomain(clientDomain);
    setClientName(clientName);
    setSlideOverOpen(slideOver);
    setClientOrgId(clientOrgId);
    setSubOrgId(subOrgId);
  };

  const columns = getColumns();

  const onPageChange = (value: number) => {
    setPageNumber(value);
  };

  const onPaginationChange = (pageNumber: number) => {
    onPageChange(pageNumber + 1);
  };

  useEffect(() => {
    if (!isSlideOverOpen) {
      setSlideOverOpen(false);
      setClientDomain("");
      setClientOrgId("");
    }
  }, [isSlideOverOpen]);

  const handleClientSlideOverClose = () => {
    setSlideOverOpen(false);
    setClientDomain("");
    setClientOrgId("");
    sessionStorage.removeItem("selectedClient");
  };
  return (
    <div >
      <Box sx={{ width: "100%", height: "100%" }}>
        <BasicDatatable
          columns={columns}
          rows={rows}
          rowCount={totalRecord}
          initialSort={initialSort}
          onPageSizeChange={(newPageSize) => {
            if (newPageSize) handlePageSizeChange(newPageSize);
          }}
          onSortingChange={onSortingChange}
          onPaginationChange={onPaginationChange}
          minHeight="400px"
          isBordervisible={true}
          columnVisibility={{
            managerTime: false,
            analystTime: false,
            managerTalkTime:false,
            analystTalkTime: false,
          }}
        />
        <ProgressSpinner showSpinner={showSpinner} />
      </Box>
      <SlideOver isOpen={slideOverOpen} onClose={handleClientSlideOverClose}>
        <ClientInteractionActionView
          clientDomain={clientDomain}
          isSlideOverOpen={isSlideOverOpen}
          removeCustomDateRange={removeCustomDateRange}
          slideOverParent={slideOverParent}
          activeStat = {activeStat}
          selectedUser = {selectedUser}
          parentPageIdentifier = {parentPageIdentifier}
          relationshipType="client"
          customDateRange={customDateRange}
          userDetails={ {
            userEmail: userEmail || "",
            userProfileOrgId: ""
          } }
          clientOrgId={clientOrgId}
          subOrgId={subOrgId}
          clientName={clientName}
        />
      </SlideOver>
    </div>
  );
};

export default BusinessStatsSlideoverClient;
